import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { IngredientCardV2 } from '@sg/garnish';
import { IngredientKind } from '@sg/graphql-schema';
import { type CustomizationMachineTypes } from '@sg/shared-fe/features/customization';

import { INGREDIENT_IMAGE_WIDTH } from '../../../../constants';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsIngredientCard = (
  props: ProductDetailsIngredientCardProps,
) => {
  const {
    ingredientId,
    dietaryRestrictionNote,
    modification,
    quantity,
    activeStepperId,
    isAtMaxQuantity,
    onIncrement,
    onDecrement,
    setActiveStepperId,
    resetStepperTimeout,
  } = props;

  const isOutOfStock = modification.outOfStock;
  const isDressing = modification.ingredient?.kind === IngredientKind.Dressing;
  const isStepperActive = activeStepperId === ingredientId;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const accessibilityLabel = formatMessage(messages.a11yCardLabel, {
    quantity,
    ingredient_name: modification.ingredient?.name,
  });

  // ─────────────────────────────────────────────────────────────────────

  const handleIncrement = useCallback(() => {
    onIncrement(ingredientId);
    resetStepperTimeout();
  }, [ingredientId, onIncrement, resetStepperTimeout]);

  const handleDecrement = useCallback(() => {
    onDecrement(ingredientId);
    resetStepperTimeout();
  }, [ingredientId, onDecrement, resetStepperTimeout]);

  const handlePress = useCallback(() => {
    // Close the stepper if the customer taps the same ingredient again.
    if (activeStepperId === ingredientId) {
      setActiveStepperId(null);

      return;
    }

    // Do not use the stepper for dressings, these use a checkmark instead.
    if (isDressing && quantity === 0) {
      onIncrement(ingredientId);

      return;
    }

    // When the dressing checkmark is already check, uncheck it.
    if (isDressing && quantity !== 0) {
      onDecrement(ingredientId);

      return;
    }

    // Automatically increment modification if it is not selected, triggering the limitations banner.
    if (quantity === 0) {
      onIncrement(ingredientId);
    }

    // Do not open the stepper for new modifications if they can't be added.
    if (quantity === 0 && isAtMaxQuantity) return;

    setActiveStepperId(modification.ingredient?.id ?? null);
    resetStepperTimeout();
  }, [
    activeStepperId,
    ingredientId,
    isDressing,
    quantity,
    isAtMaxQuantity,
    setActiveStepperId,
    modification.ingredient?.id,
    resetStepperTimeout,
    onIncrement,
    onDecrement,
  ]);

  if (!modification.ingredient) {
    return null;
  }

  return (
    <IngredientCardV2.Pressable
      accessibilityLabel={accessibilityLabel}
      disabled={isOutOfStock}
      onPress={handlePress}
    >
      <IngredientCardV2.ImageContainer>
        <IngredientCardV2.Image
          source={modification.ingredient.asset.url}
          accessibilityLabel={modification.ingredient.name}
          imageWidth={INGREDIENT_IMAGE_WIDTH}
        />

        {isStepperActive ? (
          <IngredientCardV2.QuantityStepper.Container>
            <IngredientCardV2.QuantityStepper.StepperButton
              variation="decrease"
              onPress={handleDecrement}
              accessibilityLabel={formatMessage(messages.a11yDecrementLabel)}
            />

            <IngredientCardV2.QuantityStepper.Label quantity={quantity} />

            <IngredientCardV2.QuantityStepper.StepperButton
              variation="increase"
              onPress={handleIncrement}
              accessibilityLabel={formatMessage(messages.a11yIncrementLabel)}
            />
          </IngredientCardV2.QuantityStepper.Container>
        ) : null}

        {!isStepperActive && quantity > 0 ? (
          <IngredientCardV2.Quantity shouldUseIcon={isDressing}>
            {quantity}
          </IngredientCardV2.Quantity>
        ) : null}
      </IngredientCardV2.ImageContainer>

      <IngredientCardV2.Label>
        {modification.ingredient.name}
      </IngredientCardV2.Label>

      {isOutOfStock ? (
        <IngredientCardV2.NoticeLabel>
          <FormattedMessage {...messages.unavailableLabel} />
        </IngredientCardV2.NoticeLabel>
      ) : null}

      {dietaryRestrictionNote && !isOutOfStock ? (
        <IngredientCardV2.NoticeLabel>
          {dietaryRestrictionNote}
        </IngredientCardV2.NoticeLabel>
      ) : null}
    </IngredientCardV2.Pressable>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yCardLabel: {
    defaultMessage: `{quantity, select,
      0 {Add {ingredient_name}}
      other {Increase quantity of {ingredient_name} (current quantity: {quantity})}
    }`,
    description: 'Customization | Ingredient card | a11y label',
  },
  a11yIncrementLabel: {
    defaultMessage: 'Increase product quantity',
    description:
      'Customization | Ingredient card increment button | a11y label',
  },
  a11yDecrementLabel: {
    defaultMessage: 'Decrease product quantity',
    description:
      'Customization | Ingredient card decrement button | a11y label',
  },
  unavailableLabel: {
    defaultMessage: 'Unavailable',
    description: 'Customization | Ingredient card | Unavailable label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ProductDetailsIngredientCardProps = {
  ingredientId: string;
  dietaryRestrictionNote: string;
  modification: CustomizationMachineTypes.ProductModification;
  quantity: number;
  palette?: 'kale' | 'spinach';
  activeStepperId: string | null;
  isAtMaxQuantity: boolean;
  onIncrement: (ingredientId: string) => void;
  onDecrement: (ingredientId: string) => void;
  setActiveStepperId: (id: string | null) => void;
  resetStepperTimeout: () => void;
};
